import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';

class GearMoments extends Component {
  requiredRefs = ['track'];
  #abort;
  #resize;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#dispose();
    super.disconnectedCallback();
  }

  #dispose() {
    this.#abort?.abort();
    this.#resize?.disconnect();
    for (const video of this.querySelectorAll('video')) video.pause();
  }

  #initialize() {
    this.#dispose();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.refs.track.addEventListener('keydown', this.#onKey, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#pauseVideos, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    this.#resize = new ResizeObserver(this.#syncArrows);
    this.#resize.observe(this.refs.track);
    this.#syncArrows();
  }

  #syncArrows = () => {
    const { track, previous, next } = this.refs;
    const fits = track.scrollWidth <= track.clientWidth + 2;
    if (previous) previous.hidden = fits;
    if (next) next.hidden = fits;
  };

  #pauseVideos = () => {
    for (const video of this.querySelectorAll('video')) video.pause();
  };

  #move(direction) {
    const { track } = this.refs;
    const cards = [...track.querySelectorAll('[data-moment-card]')];
    if (!cards.length) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const logicalPosition = Math.abs(track.scrollLeft);
    const max = track.scrollWidth - track.clientWidth;
    const step = cards[1] ? Math.abs(cards[1].offsetLeft - cards[0].offsetLeft) : track.clientWidth;
    let target = logicalPosition + direction * step;
    if (direction > 0 && logicalPosition >= max - 3) target = 0;
    else if (direction < 0 && logicalPosition <= 3) target = max;
    target = Math.min(max, Math.max(0, target));
    track.scrollTo({ left: target * (rtl ? -1 : 1), behavior: this.#motion.matches ? 'instant' : 'smooth' });
  }

  previous() { this.#move(-1); }
  next() { this.#move(1); }

  #onKey = (event) => {
    if (event.target !== this.refs.track) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const track = this.refs.track;
      track.scrollTo({ left: event.key === 'Home' ? 0 : (track.scrollWidth - track.clientWidth) * (rtl ? -1 : 1), behavior: 'instant' });
    }
  };

  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest('[data-moment-card]');
    if (!card || !this.contains(card)) return;
    const track = this.refs.track;
    const delta = card.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollTo({ left: track.scrollLeft + delta, behavior: 'instant' });
  };
}

if (!customElements.get('cgl-gear-moments')) customElements.define('cgl-gear-moments', GearMoments);
